import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { MatchListControl } from '@/components/tree-dnd/tree-dnd-conditions/match-list-control';
import { MatchModeInlineHelp } from '@/components/tree-dnd/tree-dnd-conditions/match-mode-inline-help';
import { MOCK_LOCATION_OPTIONS } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import {
  useMatchListSummary,
  useNaturalMatchListSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface LocationConditionProps {
  node: Extract<TreeConditionNode, { kind: 'location' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'location' }>['args']
  ) => void;
}

export function LocationCondition({ node, onChange }: LocationConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const summaryDetail = useMatchListSummary(
    value.locations,
    MOCK_LOCATION_OPTIONS,
    LL.audiences.filters.conditionSummary.noLocationsSelected()
  );
  const locationsNaturalSummary = useNaturalMatchListSummary(
    value.locations,
    MOCK_LOCATION_OPTIONS,
    LL.audiences.filters.conditionSummary.noLocationsSelected()
  );
  const conditionLabel = LL.audiences.filters.conditions.location();

  const filterSummary = useMemo(() => {
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.locationExcludeSummary({
        locations: locationsNaturalSummary,
      });
    }
    return LL.audiences.filters.conditionSummary.locationIncludeSummary({
      locations: locationsNaturalSummary,
    });
  }, [LL, locationsNaturalSummary, node.negated]);

  const hasSelectedLocations = value.locations.ids.length > 0;
  const preview = useConditionAudiencePreview(
    node,
    open && hasSelectedLocations
  );

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.locationDescription()}
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            error={
              hasSelectedLocations
                ? undefined
                : LL.audiences.filters.editSheet.noLocationsSelectedError()
            }
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <ConditionField
          label={LL.audiences.filters.editSheet.locationsLabel()}
          helpPlacement="before"
          helpText={
            <MatchModeInlineHelp
              match={value.locations.match}
              onChange={(match) =>
                onChange({
                  ...value,
                  locations: { ...value.locations, match },
                })
              }
              before={LL.audiences.filters.conditionSummary.locationMatchHelpBefore()}
              afterAny={LL.audiences.filters.conditionSummary.locationMatchHelpAfterAny()}
              afterAll={LL.audiences.filters.conditionSummary.locationMatchHelpAfterAll()}
            />
          }
        >
          <MatchListControl
            variant="expanded"
            value={value.locations}
            onChange={(locations) => onChange({ ...value, locations })}
            options={MOCK_LOCATION_OPTIONS}
            allowAll={false}
          />
        </ConditionField>
      </ConditionEditSheet>
    </>
  );
}
